// "Mission Briefing" — shown when starting the timer on a project whose customer
// has a goal. Reminds the worker of the north-star and every criterion's status,
// then asks for a one-tap confirmation before the timer runs.
import { Modal } from './components.jsx';
import { IconFlag, IconPlay } from './icons.jsx';
import {
  currentMonthKey, metricMonthTarget, metricActual, metricPct, metricStatus,
  worstStatus, STATUS_META, fmtGoalValue, fmtCadence,
} from './lib/goals.js';
import { deadlineDaysLeft } from './lib/format.js';

function GoalBriefingModal({ project, goal, metrics, progressByMetric, onConfirm, onClose }) {
  const mk = currentMonthKey();
  const list = metrics || [];
  const statuses = list.map(m => metricStatus(m, goal, progressByMetric[m.id] || [], mk));
  const overall = STATUS_META[worstStatus(statuses)];
  const daysLeft = deadlineDaysLeft(goal.target_date);

  return (
    <Modal onClose={onClose}>
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', right: -70, top: -70, width: 240, height: 240,
          background: `radial-gradient(closest-side, ${overall.soft}, transparent)`, pointerEvents: 'none',
        }}/>
        <div style={{ padding: '22px 24px 4px' }}>
          <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconFlag size={12}/> Darum geht's · {project.title}
          </div>
          <h2 className="serif" style={{ fontSize: 23, fontWeight: 500, margin: '8px 0 0', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
            {goal.title}
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            <span className="chip" style={{ height: 24, color: overall.color, background: overall.soft, borderColor: 'transparent' }}>
              {overall.emoji} {overall.label}
            </span>
            {daysLeft != null && (
              <span className="chip" style={{ height: 24 }}>⏳ noch {daysLeft > 0 ? `${daysLeft} Tage` : 'überfällig'}</span>
            )}
          </div>
        </div>

        <div style={{ padding: '16px 24px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map(m => {
            const rows = progressByMetric[m.id] || [];
            const actual = metricActual(m, rows, mk);
            const pct = metricPct(m, goal, rows, mk);
            const meta = STATUS_META[metricStatus(m, goal, rows, mk)];
            const isRecurring = m.type === 'recurring';
            const headlineTarget = isRecurring ? metricMonthTarget(m, goal, mk) : (Number(m.target_value) || 0);
            return (
              <div key={m.id} style={{ padding: 12, background: 'var(--bg-sunken)', border: '1px solid var(--stroke)', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {m.label}
                    {isRecurring && <span className="tiny muted" style={{ fontWeight: 500 }}> · {fmtCadence(m)}</span>}
                  </div>
                  <div className="tiny mono" style={{ color: meta.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {fmtGoalValue(actual, m.unit)} / {fmtGoalValue(headlineTarget, m.unit)}{isRecurring ? ' (Monat)' : ' (Ziel)'}
                  </div>
                </div>
                <div className="bar thick"><span style={{ width: `${pct}%`, background: meta.color }}/></div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '14px 24px 22px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <div style={{ flex: 1 }}/>
          <button className="btn btn-primary" onClick={onConfirm} style={{ fontSize: 14, padding: '10px 18px' }}>
            <IconPlay size={13}/> Los geht's 💪
          </button>
        </div>
      </div>
    </Modal>
  );
}

export { GoalBriefingModal };
